import { useMemo, useState } from "react";
import { Accordion, Col, Container, Form, Row } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { PageTitleBlock } from "components/layout/page-title-block";
import { LoadingWrapper } from "components/layout/loading-wrapper";
import { PageBottomPagination } from "components/pagination/page-bottom-pagination";
import { getPageItems } from "components/pagination/helpers";
import { ClientProfileProductItem } from "components/client-profile/client-profile-product-item";
import { useGetClientProfileQuery, useGetProductsQuery } from "api/ecosternApi";
import { useAppSelector } from "store/hooks";
import { selectCurrentClientId } from "store/slices/userSlice";

const PERIOD_OPTIONS = [30, 90, 180, 365];
const DEFAULT_PERIOD = 180;
const PAGE_SIZE = 20;

enum ProductFilter {
    All = "clientProfile.filter.all",
    NeverOrdered = "clientProfile.filter.neverOrdered",
    Ordered = "clientProfile.filter.ordered",
}

enum SortOption {
    Interest = "clientProfile.sort.interest",
    LastActivity = "clientProfile.sort.lastActivity",
}

interface IClientProfileViewState {
    days: number;
    filter: ProductFilter;
    sortOption: SortOption;
    currentPage: number;
}

export const ClientProfileView = () => {
    const intl = useIntl();
    const clientId = useAppSelector(selectCurrentClientId);
    const [state, setState] = useState<IClientProfileViewState>({
        days: DEFAULT_PERIOD,
        filter: ProductFilter.All,
        sortOption: SortOption.Interest,
        currentPage: 1,
    });
    const { data: products = [] } = useGetProductsQuery(clientId ?? skipToken);
    const from = useMemo(() => {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        date.setDate(date.getDate() - state.days);
        return date.toISOString();
    }, [state.days]);
    const { data: profile, isFetching, isError } = useGetClientProfileQuery(clientId ? { clientId, from } : skipToken);

    const rows = useMemo(() => {
        if (!profile) return [];

        let result = profile.products;
        if (state.filter !== ProductFilter.All) {
            result = result.filter((row) => (state.filter === ProductFilter.Ordered) === row.orderCount > 0);
        }
        if (state.sortOption === SortOption.LastActivity) {
            result = [...result].sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt));
        }
        return result;
    }, [profile, state.filter, state.sortOption]);

    const updateState = (partialState: Partial<IClientProfileViewState>) => {
        setState({ ...state, ...partialState, currentPage: partialState.currentPage ?? 1 });
    };

    return (
        <>
            <PageTitleBlock routeName={"clientProfile"} />
            <Container className={"pb-5 mb-2 mt-4 mb-md-4"}>
                <Row className={"g-2 align-items-center mb-3"}>
                    <Col xs={12} md={"auto"}>
                        <Form.Select size={"sm"} value={state.filter} onChange={(e) => updateState({ filter: e.target.value as ProductFilter })}>
                            {Object.values(ProductFilter).map((filter) => (
                                <option key={filter} value={filter}>
                                    {intl.formatMessage({ id: filter })}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col xs={6} md={"auto"}>
                        <Form.Select size={"sm"} value={state.sortOption} onChange={(e) => updateState({ sortOption: e.target.value as SortOption })}>
                            {Object.values(SortOption).map((option) => (
                                <option key={option} value={option}>
                                    {intl.formatMessage({ id: option })}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col xs={6} md={"auto"}>
                        <Form.Select size={"sm"} value={state.days} onChange={(e) => updateState({ days: Number(e.target.value) })}>
                            {PERIOD_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                    {intl.formatMessage({ id: "clientProfile.days" }, { days: option })}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                </Row>
                <LoadingWrapper isLoading={isFetching} isError={isError}>
                    {rows.length === 0 ? (
                        <div className={"text-muted py-4"}>
                            <FormattedMessage id={"clientProfile.empty"} />
                        </div>
                    ) : (
                        <Accordion>
                            {getPageItems(rows, state.currentPage, PAGE_SIZE).map((row) => (
                                <ClientProfileProductItem key={row.productId} row={row} product={products.find((p) => p.id === row.productId)} />
                            ))}
                        </Accordion>
                    )}
                    <hr className={"my-3"} />
                    <PageBottomPagination
                        totalItems={rows.length}
                        pageSize={PAGE_SIZE}
                        currentPage={state.currentPage}
                        onPageClick={(currentPage) => updateState({ currentPage })}
                    />
                </LoadingWrapper>
            </Container>
        </>
    );
};
